import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { Project, User } from '../../types';
import { Button } from '../common/Button';
import { ParticipantSelector } from '../Tasks/ParticipantSelector';
import { usersApi, projectsApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

const PROJECT_COLORS = [
  { name: 'Azul', value: '#3B82F6' },
  { name: 'Verde', value: '#00C875' },
  { name: 'Laranja', value: '#FDAB3D' },
  { name: 'Vermelho', value: '#E2445C' },
  { name: 'Roxo', value: '#784BD1' },
  { name: 'Rosa', value: '#FF5AC4' },
  { name: 'Ciano', value: '#66CCFF' },
  { name: 'Amarelo', value: '#FFCB00' },
  { name: 'Verde escuro', value: '#037F4C' },
  { name: 'Coral', value: '#FF7575' },
  { name: 'Lilás', value: '#A25DDC' },
  { name: 'Cinza', value: '#7F8497' },
] as const;

interface ProjectFormProps {
  initialData?: Partial<Project>;
  onSubmit: (data: Partial<Project>, addedMemberIds?: string[], removedMemberIds?: string[]) => Promise<void>;
  onCancel: () => void;
}

interface ProjectFormData {
  name: string;
  description: string;
  management_code: string;
  status: Project['status'];
  priority: Project['priority'];
  start_date: string;
  end_date: string;
  color: string;
}

export function ProjectForm({ initialData, onSubmit, onCancel }: ProjectFormProps) {
  const isCreate = !initialData?.id;
  const currentUser = useAuthStore(state => state.user);
  const canManageMembers = isCreate
    || currentUser?.id === initialData?.owner_id
    || currentUser?.role === 'admin'
    || currentUser?.role === 'super_admin';
  const [loading, setLoading] = useState(false);
  const [orgUsers, setOrgUsers] = useState<User[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [originalMemberIds, setOriginalMemberIds] = useState<string[]>([]);
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    management_code: '',
    status: 'planning' as Project['status'],
    priority: 'medium' as Project['priority'],
    start_date: '',
    end_date: '',
    color: PROJECT_COLORS[0].value,
  });

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        name: initialData.name || '',
        description: initialData.description || '',
        management_code: initialData.management_code || '',
        status: initialData.status || 'planning',
        priority: initialData.priority || 'medium',
        start_date: initialData.start_date?.split('T')[0] || '',
        end_date: initialData.end_date?.split('T')[0] || '',
        color: initialData.color || PROJECT_COLORS[0].value,
      }));
    }
  }, [initialData]);

  useEffect(() => {
    if (!canManageMembers) return;
    usersApi.getAll()
      .then(res => setOrgUsers(res.data))
      .catch(() => setOrgUsers([]));
  }, [canManageMembers]);

  useEffect(() => {
    if (isCreate || !canManageMembers || !initialData?.id) return;
    projectsApi.getMembers(initialData.id)
      .then(res => {
        const ids = res.data.map((member: { user_id: string }) => member.user_id);
        setOriginalMemberIds(ids);
        setSelectedMemberIds(ids);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, canManageMembers, initialData?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isCreate) {
        await onSubmit(formData, selectedMemberIds);
      } else if (canManageMembers) {
        const added = selectedMemberIds.filter(id => !originalMemberIds.includes(id));
        const removed = originalMemberIds.filter(id => !selectedMemberIds.includes(id));
        await onSubmit(formData, added, removed);
      } else {
        await onSubmit(formData);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Projeto *</label>
        <input
          type="text"
          value={formData.name}
          onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Digite o nome do projeto"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Codigo gerencial</label>
        <input
          type="text"
          value={formData.management_code}
          onChange={e => setFormData(prev => ({ ...prev, management_code: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Ex.: RESTAURAGRO-2026"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
        <textarea
          value={formData.description}
          onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          rows={3}
          placeholder="Descrição do projeto..."
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={formData.status}
            onChange={e => setFormData(prev => ({ ...prev, status: e.target.value as Project['status'] }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="planning">Planejamento</option>
            <option value="in_progress">Em Andamento</option>
            <option value="completed">Concluído</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
          <select
            value={formData.priority}
            onChange={e => setFormData(prev => ({ ...prev, priority: e.target.value as Project['priority'] }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="low">Baixa</option>
            <option value="medium">Média</option>
            <option value="high">Alta</option>
            <option value="critical">Crítica</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data de Início</label>
          <input
            type="date"
            value={formData.start_date}
            onChange={e => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data de Término</label>
          <input
            type="date"
            value={formData.end_date}
            onChange={e => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Cor</label>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Cor do projeto">
          {PROJECT_COLORS.map(({ name, value }) => {
            const isSelected = formData.color === value;

            return (
              <button
                key={value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, color: value }))}
                className={`flex h-8 w-8 items-center justify-center rounded-full border-2 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  isSelected ? 'scale-110 border-gray-700 shadow-sm' : 'border-transparent'
                }`}
                style={{ backgroundColor: value }}
                aria-label={name}
                aria-pressed={isSelected}
                title={name}
              >
                {isSelected && <Check size={16} className="text-white drop-shadow" strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      </div>

      {canManageMembers && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Membros do projeto</label>
          <ParticipantSelector
            members={orgUsers.filter(user => user.id !== currentUser?.id && user.id !== initialData?.owner_id)}
            selectedIds={selectedMemberIds}
            onChange={setSelectedMemberIds}
          />
          <p className="mt-1 text-xs text-gray-400">
            {isCreate ? 'Você é adicionado automaticamente como responsável.' : 'O responsável pelo projeto não é exibido aqui e não pode ser removido.'}
          </p>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button type="submit" loading={loading} className="flex-1">
          {initialData?.id ? 'Atualizar Projeto' : 'Criar Projeto'}
        </Button>
      </div>
    </form>
  );
}
